import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Container, Box } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import './App.css';

// Auth context provider
import { AuthProvider } from './context/AuthContext';

// Components
import Navbar from './components/Navbar';
import PrivateRoute from './components/PrivateRoute';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import InventoryDashboard from './pages/InventoryDashboard';
import AddEditItemPage from './pages/AddEditItemPage';
import ItemDetailsPage from './pages/ItemDetailsPage';
import NotFoundPage from './pages/NotFoundPage';
import OcrUploadPage from './pages/OcrUploadPage';
import BarcodeScanPage from './pages/BarcodeScanPage';
import ProductRecognitionPage from './pages/ProductRecognitionPage';
import ShoppingListPage from './pages/ShoppingListPage';
import BatchAddItemsPage from './pages/BatchAddItemsPage';

// Animated routes wrapper
const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public routes */}
        <Route path="/" element={
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <HomePage />
          </motion.div>
        } />
        <Route path="/login" element={
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <LoginPage />
          </motion.div>
        } />
        <Route path="/register" element={
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <RegisterPage />
          </motion.div>
        } />
        
        {/* Protected routes */}
        <Route element={<PrivateRoute />}>
          <Route path="/profile" element={
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <ProfilePage />
            </motion.div>
          } />
          <Route path="/inventory" element={
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <InventoryDashboard />
            </motion.div>
          } />
          <Route path="/inventory/add" element={
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <AddEditItemPage />
            </motion.div>
          } />
          <Route path="/inventory/batch-add" element={
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <BatchAddItemsPage />
            </motion.div>
          } />
          <Route path="/inventory/edit/:itemId" element={
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <AddEditItemPage />
            </motion.div>
          } />
          <Route path="/inventory/:itemId" element={
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <ItemDetailsPage />
            </motion.div>
          } />
          <Route path="/shopping-list" element={
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <ShoppingListPage />
            </motion.div>
          } />
          <Route path="/barcode-scan" element={
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <BarcodeScanPage />
            </motion.div>
          } />
          <Route path="/ocr-upload" element={
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <OcrUploadPage />
            </motion.div>
          } />
          <Route path="/product-recognition" element={
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <ProductRecognitionPage />
            </motion.div>
          } />
        </Route>
        
        {/* 404 route */}
        <Route path="*" element={
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <NotFoundPage />
          </motion.div>
        } />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Box className="App" sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Navbar />
          <Container 
            component="main" 
            className="main-content" 
            maxWidth="xl" 
            sx={{ 
              flexGrow: 1, 
              py: 4, 
              px: { xs: 2, sm: 3, md: 4 },
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <AnimatedRoutes />
          </Container>
          <Box 
            component="footer" 
            sx={{ 
              py: 3, 
              px: 2, 
              mt: 'auto', 
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
              fontSize: '0.875rem',
              color: 'text.secondary'
            }}
          >
            <Container maxWidth="xl">
              © {new Date().getFullYear()} HouseHoldr. All rights reserved.
            </Container>
          </Box>
        </Box>
      </Router>
    </AuthProvider>
  );
}

export default App;